import { Component, computed, ElementRef, inject, OnInit, signal, viewChild, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UserModel } from '../../../../core/models/data/user.model';
import { RoleModel } from '../../../../core/models/data/role.model';
import { UserService } from '../../../../core/services/user.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { PermissionUtilities } from '../../../../core/utilities/permission.utilities';
import { UsersFilter } from '../../../../core/filters/users.filter';
import { UserListSortOptions } from '../../../../core/enums/sort-options.enums';
import { PlatformRoles, UserStatuses } from '../../../../core/enums/data.enums';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { StaffRoleConstants } from '../../../../core/constants/staff-roles.constants';
import { PaginationConstants } from '../../../../core/constants/constants';
import { AvatarConstants } from '../../../../core/constants/avatar.constants';
import { StaffManagementMessagingConstants } from '../../../../core/constants/messaging.constants';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { AvatarComponent } from '../../../../parts/avatar/avatar.component';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { StaffUserPickerComponent } from '../../components/staff-user-picker/staff-user-picker.component';
import { StaffRoleAssignment } from '../../../../core/models/ui/staff-role-assignment.model';
import { StaffRoleOption } from '../../../../core/models/ui/staff-role-option.model';
import { UserRoleModel } from '../../../../core/models/data/user-role.model';

@Component({
  selector: 'ms-staff-management.page',
  imports: [
    CommonModule,
    FontAwesomeModule,
    AvatarComponent,
    ConfirmationModalComponent,
    InfoModalComponent,
    PageTitleComponent,
    PaginationComponent,
    SortDropdownComponent,
    StatusBadgeComponent,
    NoValuePipe,
    InViewPortDirective,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective,
    TooltipDirective,
    StaffUserPickerComponent
  ],
  templateUrl: './staff-management.page.html',
  styleUrl: './staff-management.page.scss',
  host: { "style": "display:contents" }
})
export class StaffManagementPage extends BaseListPageClass implements OnInit {
  private static readonly PICKER_MODAL = '#staffUserPickerModal';
  private static readonly PICKER_MODAL_BODY = '#staffUserPickerModal .modal-body';
  private static readonly ASSIGN_MODAL = '#assignStaffRoleConfirmationModal';
  private static readonly REMOVE_ROLES_MODAL = '#removeStaffRolesModal';
  private static readonly UNASSIGN_MODAL = '#unassignStaffRoleConfirmationModal';
  private static readonly INFO_MODAL = '#staffManagementInfoModal';

  private userService = inject(UserService);
  private authorizationService = inject(AuthorizationService);
  private authorizationStateService = inject(AuthorizationStateService);
  private userSessionService = inject(UserSessionService);
  private bootstrapService = inject(BootstrapService);

  public PlatformRoles = PlatformRoles;
  public StaffManagementMessagingConstants = StaffManagementMessagingConstants;
  public staffListSortOptions = UserListSortOptions;

  public staffList = signal<UserModel[]>([]);
  public errorMessage = signal<string | undefined>(undefined);
  public isSubmitting = signal<boolean>(false);

  // Resolved from the API by name, because role IDs are seeded data this app must not hardcode.
  // Keeps the whole RoleModel so the picker can show each role's own description.
  private resolvedRoles = signal<Map<PlatformRoles, RoleModel>>(new Map());

  // Set while the picker to confirmation hand-off is in flight; the role is the one being granted.
  public userToAssign = signal<UserModel | undefined>(undefined);
  public roleToAssign = signal<PlatformRoles | undefined>(undefined);

  // The row whose roles the Remove Roles dialog is showing, and the one assignment being removed.
  public userToManageRoles = signal<UserModel | undefined>(undefined);
  public roleToRemove = signal<UserRoleModel | undefined>(undefined);

  // Which step the picker modal is on: undefined is the user list, a user is the role step.
  public pickerUser = signal<UserModel | undefined>(undefined);

  // A roles.<role> permission is a capability ("may grant/revoke this role"), not identity: a
  // System Administrator holds every one through roles.*, a Staff Manager holds only roles.staff.
  // Checked at the user level because staff roles are never organization-scoped. Order follows
  // StaffRoleConstants, so the selector and the list's Role sort read the same way.
  public assignableRoles = computed<PlatformRoles[]>(() => StaffRoleConstants.MANAGEABLE()
    .filter(manageable => PermissionUtilities.hasUserLevelPermission(
      manageable.permission, this.authorizationService, this.authorizationStateService))
    .map(manageable => manageable.role));

  // Every role each user currently holds, keyed by user. The Role COLUMN shows only
  // UserModel.mostPermissiveRole, which rides along on the list response — this map is for the
  // decisions behind it: which removals the Remove Roles dialog offers, the authority comparison
  // each one is gated on, and which roles the picker should leave out for an already-staff user.
  private heldRoles = signal<Map<number, UserRoleModel[]>>(new Map());

  // What the picker's role step renders for the user it has selected: the roles the viewer may
  // grant, minus any that user already holds, each carrying the API's own description. System
  // Administrator only ever reaches this list for a viewer holding roles.sys_admin.
  public roleOptionsForSelectedUser = computed<StaffRoleOption[]>(() => {
    const selected = this.pickerUser();
    const alreadyHeld = selected ? this.rolesFor(selected.userId).map(userRole => userRole.name) : [];

    return this.assignableRoles()
      .filter(role => !alreadyHeld.includes(role))
      .map(role => ({ role: role, description: this.resolvedRoles().get(role)?.description }));
  });

  // The viewer's own authority — the lowest (strongest) level across their roles. Every comparison
  // below is against this, and all of them are false while it is undefined.
  private viewerAuthorityLevel = computed<number | undefined>(() =>
    this.authorizationStateService.authorizationState()?.userPermissions?.authorityLevelRequired ?? undefined);

  public avatarSize = AvatarConstants.SIZE().USER_LIST;
  public avatarShape = AvatarConstants.SHAPE().USER_LIST;
  public avatarAdditionalAttributes = AvatarConstants.ADDITIONAL_ATTRIBUTES().USER_LIST;

  public skeletonStaff: UserModel[] = [];

  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;

  @ViewChild('assignConfirmationModalRef') assignConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('unassignConfirmationModalRef') unassignConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('staffManagementInfoModalRef') infoModalRef!: InfoModalComponent;
  // A signal query, so the modal footer can react to which step the picker is on.
  public userPicker = viewChild(StaffUserPickerComponent);

  public async ngOnInit(): Promise<void> {
    this.orderBy = ListConstants.STAFF_ORDERBY_DEFAULT();
    this.sortOptions = [...ListConstants.STAFF_SORT_OPTIONS()];
    this.createSkeletonStaff();

    await this.loadStaffRoles();
    if (!this.errorMessage()) {
      await this.loadStaff();
    }
  }

  private createSkeletonStaff(): void {
    this.skeletonStaff = Array.from({ length: this.take }, (_, index) => ({
      userId: index,
      firstName: 'Loading',
      lastName: 'User',
      emailAddress: 'loading@example.com',
      status: UserStatuses.Active,
      mostPermissiveRole: StaffRoleConstants.MANAGEABLE()[0]?.role,
      lastLogin: new Date()
    } as UserModel));
  }

  // Stands the skeleton rows in while the first page is in flight, so the table doesn't flash
  // ms-pagination's "No results found" before any data arrives.
  public get displayStaff(): UserModel[] {
    const staff = this.staffList();
    return (this.isLoading && staff.length === 0) ? this.skeletonStaff : staff;
  }

  public jumpToTop(element: string): void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;

      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }

  // #region Held roles
  public rolesFor(userId: number): UserRoleModel[] {
    return this.heldRoles().get(userId) ?? [];
  }

  /**
   * Whether this particular assignment may be taken away. Two independent gates, both failing
   * CLOSED, mirroring what the API would accept:
   *
   * 1. You hold the `roles.<role>` permission for that role.
   * 2. The role is strictly WEAKER than your own strongest — the same comparison the API makes in
   *    `IsAuthorizedToModifyUsersAsync`, which rejects equals. System Administrator is
   *    authorityLevel 0 with nothing above it, so that role can never be removed by anyone.
   *
   * Never on your own account either. Rule 2 already covers it (your strongest role ties your own
   * level), but a lockout is not worth resting on a derivation from list data.
   */
  public canRemoveRole(user: UserModel, userRole: UserRoleModel): boolean {
    if (this.userSessionService.isCurrentUser(user.userId)) return false;
    if (!this.assignableRoles().includes(userRole.name)) return false;

    const viewerLevel = this.viewerAuthorityLevel();
    return viewerLevel !== undefined && userRole.authorityLevel > viewerLevel;
  }

  public removableRolesFor(user: UserModel): UserRoleModel[] {
    return this.rolesFor(user.userId).filter(userRole => this.canRemoveRole(user, userRole));
  }

  // Drives the row's Remove Roles button: no point opening a dialog that would offer nothing.
  public canRemoveAnyRole(user: UserModel): boolean {
    return this.removableRolesFor(user).length > 0;
  }

  /**
   * Why this row offers no removal — undefined when one IS available. Drives a disabled control
   * and its tooltip rather than an empty Actions cell.
   *
   * DEV-2541 set the convention of disabling on an authority block and hiding on a missing
   * permission. Hiding works where the action is one item in a dropdown that still has others;
   * here it is the row's only action, so hiding empties the cell — and for a Staff Manager that is
   * every System Administrator and Staff Manager row at once, which reads as a broken column
   * rather than a withheld action. So the control is always rendered, disabled, and says why.
   */
  public removeRolesBlockedReason(user: UserModel): string | undefined {
    const held = this.rolesFor(user.userId);
    if (held.length === 0 || this.canRemoveAnyRole(user)) return undefined;

    if (this.userSessionService.isCurrentUser(user.userId)) {
      return StaffManagementMessagingConstants.STAFF_REMOVE_BLOCKED_SELF();
    }

    // Authority is reported in preference to permission because it is the stronger reason: a role
    // at or above your own is one nobody at your level can remove, however the grants are seeded.
    const viewerLevel = this.viewerAuthorityLevel();
    const allOutrankViewer = viewerLevel === undefined
      || held.every(userRole => userRole.authorityLevel <= viewerLevel);

    return allOutrankViewer
      ? StaffManagementMessagingConstants.STAFF_REMOVE_BLOCKED_AUTHORITY()
      : StaffManagementMessagingConstants.STAFF_REMOVE_BLOCKED_PERMISSION();
  }
  // #endregion Held roles

  // #region Loading
  private async loadStaffRoles(): Promise<void> {
    try {
      const rolesResult = await this.authorizationService.getRoles(true);

      if (!rolesResult.success || !rolesResult.data) {
        this.errorMessage.set(rolesResult.message || StaffManagementMessagingConstants.STAFF_ERROR_LOADING_ROLES());
        return;
      }

      this.resolveRoles(rolesResult.data);
    } catch (error) {
      console.error(StaffManagementMessagingConstants.STAFF_ERROR_LOADING_ROLES(), error);
      this.errorMessage.set(StaffManagementMessagingConstants.STAFF_ERROR_LOADING_ROLES());
    }
  }

  // Names the roles that are missing rather than reporting a generic failure: with a longer list,
  // "Staff Supervisor could not be found" is the difference between a one-line seed fix and a hunt.
  private resolveRoles(roles: RoleModel[]): void {
    const resolved = new Map<PlatformRoles, RoleModel>();

    StaffRoleConstants.MANAGEABLE().forEach(manageable => {
      const resolvedRole = roles.find(role => role.name === manageable.role);
      if (resolvedRole) resolved.set(manageable.role, resolvedRole);
    });

    this.resolvedRoles.set(resolved);

    const missing = StaffRoleConstants.MANAGEABLE()
      .filter(manageable => !resolved.has(manageable.role))
      .map(manageable => manageable.role);

    if (missing.length > 0) {
      this.errorMessage.set(StaffManagementMessagingConstants.STAFF_ROLES_NOT_FOUND()
        .replaceValuesWithObject({ roleNames: missing.join(', ') }));
    }
  }

  // One unpaged read of the whole assignment table, which the API documents as bounded by staff
  // headcount. Refreshed with the list, so a grant or revoke is reflected everywhere at once.
  // Returns false when the assignments could not be read. The caller must stop there: every row
  // action is decided from this map, so carrying on would hide all of them on a first load and,
  // after a mutation, offer removals for roles that may no longer be held.
  private async loadHeldRoles(): Promise<boolean> {
    const result = await this.authorizationService.getUserRoles();

    if (!result.success || !result.data) {
      this.heldRoles.set(new Map());
      this.errorMessage.set(result.message || StaffManagementMessagingConstants.STAFF_ERROR_LOADING_ASSIGNMENTS());
      return false;
    }

    const byUser = new Map<number, UserRoleModel[]>();

    result.data.forEach(userRole => {
      const existing = byUser.get(userRole.userId);
      if (existing) {
        existing.push(userRole);
      } else {
        byUser.set(userRole.userId, [userRole]);
      }
    });

    // Strongest first, so a row reads the way the Role sort orders the list.
    byUser.forEach(userRoles => userRoles.sort((a, b) => a.authorityLevel - b.authorityLevel));

    this.heldRoles.set(byUser);
    return true;
  }

  public async loadStaff(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (append === false) { this.resetPagination(); }

    if (await this.loadHeldRoles() === false) {
      this.isLoading = false;
      return;
    }

    // roleIds matches a user's MOST PERMISSIVE role, so someone holding two staff roles is listed
    // once, under the stronger of them. Asking for every manageable role — not a fixed set — is what
    // keeps a newly added staff role from making its holders invisible on the page that lists them.
    const filter: UsersFilter = {
      roleIds: [...this.resolvedRoles().values()].map(role => role.roleId),
      statuses: [UserStatuses.Active, UserStatuses.Inactive, UserStatuses.Suspended]
    };

    const response = await this.userService.searchUsers(filter, this.orderBy, this.skip(), this.take);

    if (response.success === false) {
      this.errorMessage.set(response.message || StaffManagementMessagingConstants.STAFF_ERROR_LOADING_STAFF());
      this.isLoading = false;
      return;
    }

    this.errorMessage.set(undefined);
    this.totalCount = response?.data?.total ?? 0;
    this.hasNext = response?.data?.hasNext ?? false;

    const page = response?.data?.data ?? [];
    this.staffList.set(append ? this.staffList().concat(page) : page);

    this.isLoading = false;
  }

  public loadMoreInView(): void {
    if (this.canLoadMore && this.hasNext) {
      this.loadStaff(true);
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }
  // #endregion Loading

  // #region Sorting
  public toggleSortDirection(columnName: string): void {
    const columnSortMap: Record<string, [UserListSortOptions, UserListSortOptions]> = {
      "Name": [UserListSortOptions.NameASC, UserListSortOptions.NameDESC],
      "Role": [UserListSortOptions.RoleASC, UserListSortOptions.RoleDESC],
      "Status": [UserListSortOptions.StatusASC, UserListSortOptions.StatusDESC],
      "Last Login": [UserListSortOptions.LastLoginASC, UserListSortOptions.LastLoginDESC]
    };

    const [ascOption, descOption] = columnSortMap[columnName] ?? [UserListSortOptions.NameASC, UserListSortOptions.NameDESC];

    this.setSortOrder(this.orderBy === ascOption ? descOption : ascOption);
  }

  public setSortOrder(sortOption: string): void {
    this.orderBy = sortOption;
    this.loadStaff();
  }
  // #endregion Sorting

  // #region Assign
  public openUserPicker(): void {
    // The modal body keeps its scroll position between visits, so a reopened picker would otherwise
    // come back part-way down the previous list.
    document.querySelector(StaffManagementPage.PICKER_MODAL_BODY)?.scrollTo({ top: 0 });
    this.pickerUser.set(undefined);
    this.userPicker()?.open();
    this.bootstrapService.manageModal(StaffManagementPage.PICKER_MODAL, ModalActions.Show);
  }

  public onPickerUserChosen(user: UserModel): void {
    this.pickerUser.set(user);
  }

  public backToPickerUsers(): void {
    this.pickerUser.set(undefined);
  }

  public onRoleSelectedForAssignment(assignment: StaffRoleAssignment): void {
    this.userToAssign.set(assignment.user);
    this.roleToAssign.set(assignment.role);
    this.bootstrapService.manageModal(StaffManagementPage.PICKER_MODAL, ModalActions.Hide);

    this.assignConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.assignConfirmationModalRef.message.set(
      StaffManagementMessagingConstants.STAFF_ASSIGN_ROLE_CONFIRMATION().replaceValuesWithObject(
        { roleName: assignment.role, firstName: assignment.user.firstName, lastName: assignment.user.lastName })
    );
    this.assignConfirmationModalRef.confirmButtonText.set(`Assign ${assignment.role}`);
    this.assignConfirmationModalRef.confirmButtonClassName.set('btn-primary');

    this.bootstrapService.manageModal(StaffManagementPage.ASSIGN_MODAL, ModalActions.Show);
  }

  public async onAssignConfirmed(): Promise<void> {
    // The shared confirmation modal shows a spinner while submitting but leaves its confirm button
    // clickable, so a double click arrives here twice — two grants, two result modals, two reloads.
    if (this.isSubmitting()) return;

    const user = this.userToAssign();
    const roleId = this.roleIdFor(this.roleToAssign());
    if (!user || !roleId) return;

    this.isSubmitting.set(true);

    try {
      const result = await this.authorizationService.addRolesToUser(user.userId, [roleId]);
      await this.handleRoleChangeResult(result.success, result.message, user,
        StaffManagementMessagingConstants.STAFF_ERROR_ASSIGNING_ROLE().replaceValuesWithObject({ roleName: this.roleToAssign() }));
    } finally {
      this.isSubmitting.set(false);
      this.bootstrapService.manageModal(StaffManagementPage.ASSIGN_MODAL, ModalActions.Hide);
      this.bootstrapService.manageModal(StaffManagementPage.INFO_MODAL, ModalActions.Show);
    }
  }

  public onAssignCancelled(): void {
    this.bootstrapService.manageModal(StaffManagementPage.ASSIGN_MODAL, ModalActions.Hide);
    this.userToAssign.set(undefined);
    this.bootstrapService.manageModal(StaffManagementPage.PICKER_MODAL, ModalActions.Show);
  }
  // #endregion Assign

  // #region Remove roles
  // A user may hold several roles, so the row opens a dialog listing them rather than acting on
  // one implied role. The list is read live from heldRoles, so it reflects the last removal.
  public openRemoveRoles(user: UserModel): void {
    this.userToManageRoles.set(user);
    this.bootstrapService.manageModal(StaffManagementPage.REMOVE_ROLES_MODAL, ModalActions.Show);
  }

  public confirmRemoveRole(userRole: UserRoleModel): void {
    const user = this.userToManageRoles();
    if (!user) return;

    this.roleToRemove.set(userRole);
    this.bootstrapService.manageModal(StaffManagementPage.REMOVE_ROLES_MODAL, ModalActions.Hide);

    this.unassignConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.unassignConfirmationModalRef.message.set(
      StaffManagementMessagingConstants.STAFF_UNASSIGN_ROLE_CONFIRMATION().replaceValuesWithObject(
        { roleName: userRole.name, firstName: user.firstName, lastName: user.lastName })
    );
    this.unassignConfirmationModalRef.confirmButtonText.set(`Remove ${userRole.name}`);
    this.unassignConfirmationModalRef.confirmButtonClassName.set('btn-danger');

    this.bootstrapService.manageModal(StaffManagementPage.UNASSIGN_MODAL, ModalActions.Show);
  }

  public async onUnassignConfirmed(): Promise<void> {
    // Same re-entry guard as the grant: a second click would send the removal again, and the
    // repeat 404 would overwrite the first attempt's success with an error.
    if (this.isSubmitting()) return;

    const user = this.userToManageRoles();
    const userRole = this.roleToRemove();
    if (!user || !userRole) return;

    this.isSubmitting.set(true);

    try {
      const result = await this.authorizationService.removeRolesFromUser(user.userId, [userRole.roleId]);
      await this.handleRoleChangeResult(result.success, result.message, user,
        StaffManagementMessagingConstants.STAFF_ERROR_UNASSIGNING_ROLE().replaceValuesWithObject({ roleName: userRole.name }));
    } finally {
      this.isSubmitting.set(false);
      this.bootstrapService.manageModal(StaffManagementPage.UNASSIGN_MODAL, ModalActions.Hide);
      this.bootstrapService.manageModal(StaffManagementPage.INFO_MODAL, ModalActions.Show);
    }
  }

  // Back to the role list, which by then no longer offers the role just removed.
  public onUnassignCancelled(): void {
    this.roleToRemove.set(undefined);
    this.bootstrapService.manageModal(StaffManagementPage.UNASSIGN_MODAL, ModalActions.Hide);
    this.bootstrapService.manageModal(StaffManagementPage.REMOVE_ROLES_MODAL, ModalActions.Show);
  }
  // #endregion Remove roles


  // undefined for an unmanaged role, which the assign/unassign guards already treat as "do
  // nothing" — so an unknown role can never be sent to the API under another role's id.
  private roleIdFor(role: PlatformRoles | undefined): number | undefined {
    return role === undefined ? undefined : this.resolvedRoles().get(role)?.roleId;
  }

  private async handleRoleChangeResult(success: boolean | undefined, message: string | undefined, target: UserModel, fallbackMessage: string): Promise<void> {
    const succeeded = success === true;

    this.infoModalRef.modalType.set(succeeded ? ModalTypes.Success : ModalTypes.Failure);
    this.infoModalRef.message.set(message ?? fallbackMessage);

    if (!succeeded) return;

    // Granting or revoking the acting user's own role changes what they may do here, so their
    // permission context has to be re-read before the page renders its next set of actions.
    if (this.userSessionService.isCurrentUser(target.userId)) {
      await this.authorizationStateService.refreshContext();
    }

    await this.loadStaff();
  }
}
